import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Col } from 'antd';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TMealPlanItem } from 'interfaces/mealPlan';
import { useSaveMealPlanImage } from 'hooks';

import NutritionModal from './NutritionModal';
import { MealPlanCardWrapper, MealPlanThumbnail, MealPlanButtonGroup } from './styles';

interface TImageData {
  file: File;
  previewURL: string;
}
interface MenuPlanCardProps {
  mealPlanIdx: number;
  mealPlanName: string;
  mealPlanImage: string;
  mealPlanItems: TMealPlanItem[];
  mealPlanContent: string;
  nutritionInfo: {
    totalCalories: number;
    totalCarbohydrate: number;
    totalFat: number;
    totalNatrium: number;
    totalProtein: number;
    totalSugar: number;
  };
}

const MenuPlanCard = ({
  mealPlanIdx,
  mealPlanName,
  mealPlanImage,
  mealPlanItems,
  mealPlanContent,
  nutritionInfo
}: MenuPlanCardProps) => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>();
  const [imageInfo, setImageInfo] = useState<TImageData>({ file: null, previewURL: mealPlanImage || '' });
  const [isIngredientModal, setIsIngredientModal] = useState<boolean>(false);

  const { mutateAsync: saveMealPlanImage } = useSaveMealPlanImage();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging, index, newIndex } = useSortable({
    id: mealPlanIdx
  });

  const imageUploadOnClick = () => {
    if (index === newIndex) {
      inputRef?.current?.click();
    }
  };

  const uploadImage = async (imageFileData: TImageData) => {
    const formData = new FormData();
    formData.append('mealPlanImage', imageFileData.file);

    await saveMealPlanImage({ mealPlanIdx, params: formData });
    setImageInfo(imageFileData);
  };

  const handleImageDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    if (!imageInfo?.previewURL) {
      event.preventDefault();
      const item = event.dataTransfer.items[0];
      const file = item.getAsFile();
      uploadImage({ file, previewURL: URL.createObjectURL(file) });
    }
  };

  const handleImageChange = async ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    if (!imageInfo?.previewURL) {
      const file = target.files[0];
      uploadImage({ file, previewURL: URL.createObjectURL(file) });
    }
  };

  const buttonMouseDown = (event: React.MouseEvent<Element>) => event.stopPropagation();

  const handleOnOffIngredientModal = () => setIsIngredientModal(!isIngredientModal);

  const handleMenuPlanEdit = () => navigate(`/mealPlan/${mealPlanIdx}`);

  return (
    <Col
      ref={setNodeRef}
      span={6}
      style={{
        minWidth: '430px',
        maxWidth: '430px',
        height: '346px',
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 999999 : 1
      }}
    >
      <MealPlanCardWrapper {...listeners} {...attributes}>
        <MealPlanThumbnail role="presentation" onPointerUp={imageUploadOnClick} onDrop={handleImageDrop}>
          {!imageInfo?.previewURL ? <input type="file" accept="image/*" ref={inputRef} onChange={handleImageChange} /> : null}
          {!imageInfo?.previewURL ? (
            <div className="non-image-description">
              <div>이미지를 등록해주세요</div>
              <div>드래그 앤 드랍 또는 클릭</div>
              <div className="image-guide-description">(이미지 권장 사이즈 400 x 300)</div>
            </div>
          ) : (
            <img alt="previewImage" className="preview-image" draggable={false} src={imageInfo?.previewURL} />
          )}
        </MealPlanThumbnail>
        <div className="menu-plan-name">{mealPlanName}</div>
        <MealPlanButtonGroup>
          <Button type="primary" onClick={handleOnOffIngredientModal} onMouseDown={buttonMouseDown}>
            식단구성
          </Button>
          <Button type="primary" className="edit-button" onClick={handleMenuPlanEdit} onMouseDown={buttonMouseDown}>
            수정하기
          </Button>
        </MealPlanButtonGroup>
      </MealPlanCardWrapper>
      <NutritionModal
        open={isIngredientModal}
        mealPlanContent={mealPlanContent}
        mealPlanItems={mealPlanItems}
        mealPlanName={mealPlanName}
        nutritionInfo={nutritionInfo}
        onCancel={handleOnOffIngredientModal}
      />
    </Col>
  );
};

export default MenuPlanCard;
